/**
 * useDocumentReadability
 *
 * Live "is this document actually readable" assessment for the camera
 * viewfinder.
 *
 * The edge detectors (classical + DocAligner ML) locate a slip even when it
 * is a metre away — at that distance a till slip occupies ~6% of the frame
 * width and its text is sub-pixel, so server-side OCR (total-vision) returns
 * nothing. This hook measures whether the detected document region actually
 * resolves text-scale detail and reports a readability verdict that the
 * CameraModal uses to gate capture and guide the user ("move closer").
 *
 * Signals (all computed inside the detected document region, not the full
 * frame):
 *  - textDensity: fraction of pixels whose Sobel gradient magnitude exceeds
 *    a stroke threshold. Printed text produces a dense field of strong
 *    micro-edges; a distant or out-of-focus slip produces almost none. This
 *    is a proxy for "could OCR resolve characters here" without running OCR.
 *  - sharpness: Laplacian variance of the region (blur detection), scoped to
 *    the document rather than the whole 64px frame sample used elsewhere.
 *  - minFill: the smaller of the region's relative width/height — a low
 *    sliver floor so degenerate detections can never pass. Kept small
 *    because long till-slip segments legitimately appear as thin bands;
 *    distance is judged by density + sharpness, not fill.
 *
 * The verdict is EMA-smoothed and hysteresis-gated so the bounding box does
 * not flicker between "readable" and "move closer" at the boundary.
 *
 * @author TotalAccess Development Team
 * @date October 2026
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { DocumentEdge } from './useDocumentEdgeDetection';

// ==================== TYPES ====================

export type DocumentReadabilityReason = 'too-far' | 'blurry' | 'low-detail';

export interface DocumentReadability {
  /** Density of text-stroke gradient pixels inside the document region (0–1) */
  textDensity: number;
  /** Laplacian-variance sharpness scoped to the document region (0–1) */
  sharpness: number;
  /** Smaller of the document's relative width/height fill of the frame (0–1) */
  minFill: number;
  /** Whether the document is close/sharp enough for OCR to extract text */
  readable: boolean;
  /** Why the document is not readable (null when readable) */
  reason: DocumentReadabilityReason | null;
}

export interface UseDocumentReadabilityConfig {
  /** Whether analysis is enabled (default: true) */
  enabled?: boolean;
  /** Minimum interval between samples in ms (default: 300) */
  intervalMs?: number;
  /** EMA smoothing factor for the metrics (0–1, default: 0.5) */
  smoothing?: number;
  /** Minimum smaller-dimension frame fill required (default: 0.10).
   *  Deliberately a low sliver floor, not the distance gate — long till-slip
   *  segments legitimately appear as thin bands (a top/bottom section or the
   *  middle of a slip with only left/right edges visible), so this only
   *  rejects degenerate detections. The actual "too far to read" judgment
   *  is carried by textDensity + sharpness: beyond ~0.5m the text strokes
   *  are sub-pixel and density collapses regardless of box shape. */
  minFill?: number;
}

// ==================== ANALYSIS ====================

/** Max sample size on the region's long edge — enough to resolve text
 *  strokes on a well-framed slip while keeping Sobel/Laplacian cost small. */
const MAX_SAMPLE_EDGE = 384;
/** Inset applied to the detected region per side — keeps the document's
 *  border line and edge shadow out of the measurement. */
const REGION_INSET = 0.10;
/** Sobel |gx|+|gy| magnitude that counts as a text-stroke pixel.
 *  Sobel sums to 4×255 = 1020 for a hard black→white step; 180 ≈ a
 *  45-level luminance change over 2px — thermal print exceeds this easily,
 *  blur/distance collapses it below. */
const STROKE_GRADIENT = 180;

// Enter thresholds — ALL must hold for the region to become readable.
const ENTER_MIN_DENSITY = 0.02;
const ENTER_MIN_SHARPNESS = 0.3;
// Exit thresholds — dropping below ANY flips an already-readable region
// back to unreadable. The enter/exit gap is the hysteresis band.
const EXIT_MIN_DENSITY = 0.012;
const EXIT_MIN_SHARPNESS = 0.18;

/**
 * Crop the detected document region out of the video frame and measure
 * text-scale detail inside it. The region is sampled at NATIVE video
 * resolution capped at MAX_SAMPLE_EDGE — it is deliberately never upscaled,
 * because a distant slip's strokes are sub-pixel and upscaling only blurs
 * them further (which is exactly the signal we're measuring).
 */
function analyzeRegion(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  rect: { x: number; y: number; width: number; height: number },
): { textDensity: number; sharpness: number } | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;

  // Region in video pixels, inset to exclude the document's own edge/border.
  let rx = (rect.x + rect.width * REGION_INSET) * vw;
  let ry = (rect.y + rect.height * REGION_INSET) * vh;
  rx = Math.max(0, Math.min(vw - 8, rx));
  ry = Math.max(0, Math.min(vh - 8, ry));
  const rw = Math.max(8, Math.min(vw - rx, rect.width * (1 - 2 * REGION_INSET) * vw));
  const rh = Math.max(8, Math.min(vh - ry, rect.height * (1 - 2 * REGION_INSET) * vh));

  const scale = Math.min(1, MAX_SAMPLE_EDGE / Math.max(rw, rh));
  const sw = Math.max(8, Math.round(rw * scale));
  const sh = Math.max(8, Math.round(rh * scale));

  canvas.width = sw;
  canvas.height = sh;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  try {
    ctx.drawImage(video, rx, ry, rw, rh, 0, 0, sw, sh);
  } catch {
    return null;
  }

  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(0, 0, sw, sh);
  } catch {
    return null;
  }

  const data = imageData.data;
  const gray = new Float32Array(sw * sh);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
  }

  // Single pass over interior pixels: Sobel gradient magnitude (text-stroke
  // density) and Laplacian response (sharpness/blur).
  let strokePixels = 0;
  let lapSumSq = 0;
  let count = 0;
  for (let y = 1; y < sh - 1; y++) {
    const row = y * sw;
    for (let x = 1; x < sw - 1; x++) {
      const i = row + x;
      const gx =
        gray[i - sw + 1] + 2 * gray[i + 1] + gray[i + sw + 1] -
        gray[i - sw - 1] - 2 * gray[i - 1] - gray[i + sw - 1];
      const gy =
        gray[i + sw - 1] + 2 * gray[i + sw] + gray[i + sw + 1] -
        gray[i - sw - 1] - 2 * gray[i - sw] - gray[i - sw + 1];
      if (Math.abs(gx) + Math.abs(gy) > STROKE_GRADIENT) strokePixels++;
      const lap = -4 * gray[i] + gray[i - 1] + gray[i + 1] + gray[i - sw] + gray[i + sw];
      lapSumSq += lap * lap;
      count++;
    }
  }
  if (count === 0) return null;

  const lapVariance = lapSumSq / count;
  return {
    textDensity: strokePixels / count,
    sharpness: Math.min(1, lapVariance / (lapVariance + 200)),
  };
}

// ==================== HOOK ====================

export function useDocumentReadability(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  edge: DocumentEdge | null,
  config: UseDocumentReadabilityConfig = {},
) {
  const {
    enabled = true,
    intervalMs = 300,
    smoothing = 0.5,
    minFill = 0.10,
  } = config;

  const [readability, setReadability] = useState<DocumentReadability | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastSampleRef = useRef(0);
  const smoothedRef = useRef<{ density: number; sharpness: number } | null>(null);
  const readableRef = useRef(false);
  const verdictRef = useRef<DocumentReadability | null>(null);

  // Render-time ref mirrors — same pattern as CameraModal's documentEdgeRef:
  // keeps the rAF loop stable while always reading the latest inputs.
  const edgeRef = useRef(edge);
  edgeRef.current = edge;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const configRef = useRef({ intervalMs, smoothing, minFill });
  configRef.current = { intervalMs, smoothing, minFill };

  const tick = useCallback(() => {
    rafRef.current = requestAnimationFrame(tick);

    const currentEdge = edgeRef.current;
    if (!enabledRef.current || !currentEdge) {
      if (verdictRef.current !== null || smoothedRef.current !== null) {
        verdictRef.current = null;
        smoothedRef.current = null;
        readableRef.current = false;
        lastSampleRef.current = 0;
        setReadability(null);
      }
      return;
    }

    const { intervalMs: interval, smoothing: smooth, minFill: fill } = configRef.current;
    const now = performance.now();
    if (now - lastSampleRef.current < interval) return;
    lastSampleRef.current = now;

    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const result = analyzeRegion(video, canvasRef.current, currentEdge);
    if (!result) return;

    const prev = smoothedRef.current;
    const density = prev
      ? prev.density + (result.textDensity - prev.density) * (1 - smooth)
      : result.textDensity;
    const sharpness = prev
      ? prev.sharpness + (result.sharpness - prev.sharpness) * (1 - smooth)
      : result.sharpness;
    smoothedRef.current = { density, sharpness };

    const docMinFill = Math.min(currentEdge.width, currentEdge.height);
    const enter = docMinFill >= fill && density >= ENTER_MIN_DENSITY && sharpness >= ENTER_MIN_SHARPNESS;
    const exit = docMinFill < fill * 0.85 || density < EXIT_MIN_DENSITY || sharpness < EXIT_MIN_SHARPNESS;
    const readable = readableRef.current ? !exit : enter;
    readableRef.current = readable;

    // Reason precedence reflects the most actionable fix: a document that
    // doesn't fill enough of the frame is simply too far away; blur on a
    // well-sized document means "hold steady"; enough size + sharpness but
    // no stroke detail means the text isn't resolving (too far, or a blank
    // region misdetected as a document).
    const reason: DocumentReadabilityReason | null = readable
      ? null
      : docMinFill < fill
        ? 'too-far'
        : sharpness < ENTER_MIN_SHARPNESS
          ? 'blurry'
          : 'low-detail';

    const verdict: DocumentReadability = {
      textDensity: density,
      sharpness,
      minFill: docMinFill,
      readable,
      reason,
    };
    verdictRef.current = verdict;
    setReadability(verdict);
  }, [videoRef]);

  // Self-scheduling rAF loop — runs for the life of the hook like the edge
  // detectors. Each tick early-returns cheaply when disabled or edgeless.
  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [tick]);

  return readability;
}
